import { Add, Close, Delete } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import CustomButton from 'lib/designSystem/Button';
import { calculateDays } from 'lib/functions/dates';
import React, { useEffect, useState } from 'react';

import type { ProvisionLine, ProvisionRange } from '../../types';

interface ProvisionBuilderProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  lines?: ProvisionLine[];
  onCalculate?: (calculatedLines: ProvisionLine[], cutoffDate: string) => void;
}

const ProvisionBuilder: React.FC<ProvisionBuilderProps> = ({
  open,
  setOpen,
  lines,
  onCalculate,
}) => {
  const [ranges, setRanges] = useState<ProvisionRange[]>([]);
  const [minDays, setMinDays] = useState<string>('0');
  const [maxDays, setMaxDays] = useState<string>('0');
  const [percentage, setPercentage] = useState<string>('0');
  const [hasNoMax, setHasNoMax] = useState<boolean>(false);
  const [hasNoMin, setHasNoMin] = useState<boolean>(false);
  const [cutoffDate, setCutoffDate] = useState<string | null>(null);
  const [errors, setErrors] = useState<{
    minDays?: string;
    maxDays?: string;
    percentage?: string;
    general?: string;
  }>({});

  const handleClose = () => {
    setOpen(false);
    setRanges([]);
    setMinDays('0');
    setMaxDays('0');
    setPercentage('0');
    setHasNoMax(false);
    setHasNoMin(false);
    setCutoffDate(null);
    setErrors({});
  };

  useEffect(() => {
    if (ranges.length > 0) {
      const lastRange = ranges[ranges.length - 1];
      if (lastRange.maxDays === null) {
        return;
      }
      setMinDays((lastRange.maxDays + 1).toString());
      setHasNoMin(false);
    } else {
      setMinDays('0');
      setHasNoMin(false);
    }
  }, [ranges]);

  const handleAddRange = () => {
    setErrors({});

    const minDaysNum = minDays === '' || hasNoMin ? null : Number(minDays);
    const maxDaysNum = Number(maxDays);
    const percentageNum = Number(percentage);
    const isFirstRange = ranges.length === 0;

    const newErrors: typeof errors = {};

    if (!isFirstRange && !hasNoMin) {
      if (minDaysNum === null || isNaN(minDaysNum) || minDaysNum < 0) {
        newErrors.minDays = 'Ingrese un valor válido (mayor o igual a 0)';
      }
    }

    if (!hasNoMax) {
      if (isNaN(maxDaysNum) || maxDaysNum <= 0) {
        newErrors.maxDays = 'Ingrese un valor válido (mayor a 0)';
      }

      if (
        !hasNoMin &&
        minDaysNum !== null &&
        !isNaN(minDaysNum) &&
        minDaysNum >= maxDaysNum
      ) {
        newErrors.general =
          'Los días mínimo deben ser menores que los días máximo';
      }
    }

    if (isNaN(percentageNum) || percentageNum < 0 || percentageNum > 100) {
      newErrors.percentage = 'Ingrese un porcentaje válido (0-100)';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const newRange: ProvisionRange = {
      id: Date.now().toString(),
      minDays:
        isFirstRange && (hasNoMin || minDays === '' || minDaysNum === null)
          ? null
          : minDaysNum,
      maxDays: hasNoMax ? null : maxDaysNum,
      percentage: percentageNum,
    };

    setRanges([...ranges, newRange]);

    setMaxDays('0');
    setPercentage('0');
    setHasNoMax(false);
    setHasNoMin(false);
    setErrors({});
  };

  const handleDeleteRange = (id: string) => {
    setRanges(ranges.filter((range) => range.id !== id));
  };

  const calculateDiasMora = (
    cutoffDate: Date,
    originalPaymentDate: string,
  ): number => {
    const paymentDate = new Date(originalPaymentDate);
    return calculateDays(paymentDate, cutoffDate);
  };

  const assignTramo = (
    diasMora: number,
    ranges: ProvisionRange[],
  ): { range: ProvisionRange | null; tramoLabel: string } => {
    const sortedRanges = [...ranges].sort((a, b) => {
      if (a.minDays === null && b.minDays === null) return 0;
      if (a.minDays === null) return -1;
      if (b.minDays === null) return 1;
      return a.minDays - b.minDays;
    });

    for (const range of sortedRanges) {
      const meetsMin =
        range.minDays === null ? true : diasMora >= range.minDays;
      const meetsMax = range.maxDays === null || diasMora <= range.maxDays;

      if (meetsMin && meetsMax) {
        let tramoLabel: string;
        if (range.minDays === null) {
          if (range.maxDays === null) {
            tramoLabel = 'Todos los días';
          } else {
            tramoLabel = `Hasta ${range.maxDays} días`;
          }
        } else if (range.maxDays === null) {
          tramoLabel = `Más de ${range.minDays} días`;
        } else {
          tramoLabel = `${range.minDays}-${range.maxDays} días`;
        }
        return { range, tramoLabel };
      }
    }

    return { range: null, tramoLabel: 'Sin tramo' };
  };

  const processLines = (
    lines: ProvisionLine[],
    cutoffDateStr: string,
    ranges: ProvisionRange[],
  ): ProvisionLine[] => {
    const cutoffDate = new Date(cutoffDateStr);
    cutoffDate.setHours(12, 0, 0, 0);

    return lines.map((line) => {
      if (!line.originalPaymentDate) {
        return {
          ...line,
          cutoffDate: cutoffDateStr,
          diasMora: undefined,
          tramo: undefined,
          provisionPercentage: undefined,
          provisionAmount: undefined,
        };
      }

      const diasMora = calculateDiasMora(cutoffDate, line.originalPaymentDate);

      const { range, tramoLabel } = assignTramo(diasMora, ranges);

      const provisionPercentage = range ? range.percentage : 0;
      const realAmountInWallet = line.realAmountInWallet || 0;
      const provisionAmount = realAmountInWallet * (provisionPercentage / 100);

      return {
        ...line,
        cutoffDate: cutoffDateStr,
        diasMora,
        tramo: tramoLabel,
        provisionPercentage,
        provisionAmount,
      };
    });
  };

  const handleSave = () => {
    if (!cutoffDate || ranges.length === 0 || !lines || lines.length === 0) {
      return;
    }
    if (onCalculate) {
      const dateObj = dayjs(cutoffDate, 'DD/MM/YYYY');
      const cutoffDateISO = dateObj.isValid()
        ? dateObj.format('YYYY-MM-DD')
        : cutoffDate;

      const calculatedLines = processLines(lines, cutoffDateISO, ranges);
      onCalculate(calculatedLines, cutoffDateISO);
    }
    handleClose();
  };

  const canCalculate =
    cutoffDate !== null && ranges.length > 0 && lines && lines.length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Tabla de Tramos
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Define los tramos de días con sus porcentajes correspondientes
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Box sx={{ mb: 3 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Fecha de corte"
                format="DD/MM/YYYY"
                value={cutoffDate ? dayjs(cutoffDate, 'DD/MM/YYYY') : null}
                onChange={(newValue) => {
                  if (newValue && newValue.isValid()) {
                    setCutoffDate(newValue.format('DD/MM/YYYY'));
                  } else {
                    setCutoffDate(null);
                  }
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    helperText: !cutoffDate
                      ? 'Seleccione una fecha de corte para calcular'
                      : '',
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: 'grey.50',
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'flex-start',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ flex: 1, minWidth: '150px' }}>
                <TextField
                  label="Días mínimo"
                  type="number"
                  value={hasNoMin ? 'Sin mínimo' : minDays}
                  onChange={(e) => {
                    if (!hasNoMin) {
                      setMinDays(e.target.value);
                      setErrors({ ...errors, minDays: undefined });
                    }
                  }}
                  error={!!errors.minDays}
                  helperText={errors.minDays}
                  disabled={hasNoMin || ranges.length > 0}
                  fullWidth
                  inputProps={{ min: 0 }}
                />
                {ranges.length === 0 && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={hasNoMin}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setHasNoMin(checked);
                          if (checked) {
                            setMinDays('0');
                            setErrors({ ...errors, minDays: undefined });
                          }
                        }}
                        size="small"
                      />
                    }
                    label="Sin mínimo"
                    sx={{ mt: 0.5 }}
                  />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: '150px' }}>
                <TextField
                  label="Días máximo"
                  type="number"
                  value={hasNoMax ? 'Sin límite' : maxDays}
                  onChange={(e) => {
                    if (!hasNoMax) {
                      setMaxDays(e.target.value);
                      setErrors({ ...errors, maxDays: undefined });
                    }
                  }}
                  error={!!errors.maxDays}
                  helperText={errors.maxDays}
                  disabled={hasNoMax}
                  fullWidth
                  inputProps={{ min: 1 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={hasNoMax}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setHasNoMax(checked);
                        if (checked) {
                          setMaxDays('0');
                          setErrors({ ...errors, maxDays: undefined });
                        }
                      }}
                      size="small"
                    />
                  }
                  label="Sin máximo"
                  sx={{ mt: 0.5 }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: '150px', position: 'relative' }}>
                <TextField
                  label="Porcentaje"
                  type="number"
                  value={percentage}
                  onChange={(e) => {
                    setPercentage(e.target.value);
                    setErrors({ ...errors, percentage: undefined });
                  }}
                  error={!!errors.percentage}
                  helperText={errors.percentage}
                  fullWidth
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  InputProps={{
                    endAdornment: (
                      <Typography variant="body2" sx={{ mr: 1 }}>
                        %
                      </Typography>
                    ),
                  }}
                />
              </Box>
              <Box
                sx={{
                  alignSelf: 'flex-start',
                  minWidth: '120px',
                  position: 'relative',
                }}
              >
                <CustomButton
                  onClick={handleAddRange}
                  color="secondary"
                  startIcon={<Add />}
                  fullWidth
                  disabled={
                    ranges.length > 0 &&
                    ranges[ranges.length - 1].maxDays === null
                  }
                >
                  Agregar
                </CustomButton>
              </Box>
            </Box>
            {errors.general && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: 'block' }}
              >
                {errors.general}
              </Typography>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              backgroundColor: 'grey.50',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <TableContainer>
              <Table
                sx={{ minWidth: 650 }}
                size="small"
                aria-label="a dense table"
              >
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      Rango días
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      Porcentaje
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ranges.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        align="center"
                        sx={{ py: 3, color: 'text.secondary' }}
                      >
                        No hay tramos definidos.
                      </TableCell>
                    </TableRow>
                  ) : (
                    ranges.map((range) => (
                      <TableRow
                        key={range.id}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        <TableCell>
                          {range.minDays === null
                            ? range.maxDays === null
                              ? 'Todos los días'
                              : `Hasta ${range.maxDays} días`
                            : range.maxDays === null
                              ? `Más de ${range.minDays} días`
                              : `${range.minDays}-${range.maxDays} días`}
                        </TableCell>
                        <TableCell>{range.percentage}%</TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() => handleDeleteRange(range.id)}
                            size="small"
                            color="error"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 2 }}>
        <CustomButton
          onClick={handleClose}
          color="secondary"
          variant="outlined"
        >
          Cancelar
        </CustomButton>
        <CustomButton
          onClick={handleSave}
          color="primary"
          disabled={!canCalculate}
        >
          Calcular
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default ProvisionBuilder;
